/**
 * Transições puras do sino in-app.
 *
 * Vivem fora do store de propósito: são as decisões que definem o sino como
 * ferramenta e não como interrupção, e uma decisão dessas merece ser testável
 * sem montar store, sem mockar REST e sem renderizar nada.
 */
import type { InAppAlert, InAppAlertCreatedEvent } from '../../types/alerts'

/** True quando o alerta ainda não foi lido por este usuário. */
export function isUnread(alert: InAppAlert): boolean {
  return alert.readAt === null
}

/**
 * True quando existe pelo menos um NÃO LIDO crítico — segundo o BANCO.
 *
 * O contador de críticas vem da rota pelo mesmo motivo que `unreadCount`: a
 * lista tem teto, e a criticidade das linhas recebidas responde sobre a página,
 * não sobre o banco. Com a crítica não lida fora das linhas que chegaram, olhar
 * a página deixaria o sino apagado — e apagado não lê como "não sei", lê como
 * "não há nada urgente". Afirmação de ausência é pior que número errado.
 *
 * **O recuo para a página é deliberado, e é o único caminho honesto enquanto o
 * motor com o campo não estiver no ar:** sem `unreadCriticalCount` a resposta
 * não diz nada sobre criticidade, e a página é a melhor evidência disponível.
 * Ela erra só para MENOS (deixa de pintar), nunca inventando urgência — o lado
 * seguro de errar. Quando o campo chega, ele manda, inclusive valendo `0` sobre
 * uma página que tem crítica lida.
 */
export function hasUnreadCritical(
  alerts: readonly InAppAlert[],
  unreadCriticalCount?: number,
): boolean {
  if (unreadCriticalCount !== undefined) return unreadCriticalCount > 0
  return alerts.some((a) => isUnread(a) && a.priority === 'critical')
}

/**
 * True quando existe pelo menos um NÃO LIDO **nesta página** da lista.
 *
 * É a condição do botão "marcar todas", e ela NÃO é `unreadCount > 0`. Os dois
 * números vivem em escalas diferentes de propósito: a lista tem teto
 * (`ALERTS_LIST_LIMIT`) e o contador vem do banco inteiro — a mesma separação
 * que o motor trancou em `fd376977`. Quem tem 40 não lidos vê 30 linhas.
 *
 * O botão só alcança o que está na página, porque o motor não tem verbo de
 * lote (são 5 rotas em `/alerts`, nenhuma de marcação em massa). Amarrado a
 * `unreadCount`, ele continuaria de pé depois de marcar as 30 — um botão que
 * promete e não faz nada, para sempre. Amarrado à página, ele some quando
 * cumpriu o que podia cumprir, e volta na releitura seguinte, que traz as
 * próximas.
 */
export function hasUnreadOnPage(alerts: readonly InAppAlert[]): boolean {
  return alerts.some(isUnread)
}

/**
 * Quantos não lidos existem **fora** desta página, ou `0` quando a página os
 * cobre todos.
 *
 * Existe porque o botão "marcar todas" some quando a página acaba
 * (`hasUnreadOnPage`), e sem este aviso o sino continuaria com o numeral cheio
 * sem nada na tela explicando de onde ele vem: o número passaria a contradizer
 * a lista, e quem lê concluiria que um dos dois está quebrado.
 *
 * Nunca devolve negativo. `unreadCount` vem do banco e a página é um recorte
 * dele, mas os dois são lidos em instantes diferentes — entre uma releitura e
 * um `markRead` otimista a lista pode, por um átimo, ter mais não lidos do que
 * o contador. Um "restam -1" é pior do que não dizer nada.
 */
export function unreadBeyondPage(alerts: readonly InAppAlert[], unreadCount: number): number {
  return Math.max(0, unreadCount - alerts.filter(isUnread).length)
}

/** Devolve a lista com um item marcado como lido. Não muta a original. */
export function withReadAt(
  alerts: readonly InAppAlert[],
  id: string,
  readAt: string,
): InAppAlert[] {
  return alerts.map((a) => (a.id === id ? { ...a, readAt } : a))
}

/**
 * Devolve a lista com UM item de volta a não lido. Não muta a original.
 *
 * É o inverso pontual de `withReadAt`, e existe para que desfazer um palpite
 * otimista não precise restaurar um retrato. Restaurar retrato apaga tudo o que
 * aconteceu no meio: com duas marcações no ar e só uma falhando, devolver a
 * lista de antes do primeiro clique desmarcaria também o item do vizinho — uma
 * marcação que o motor aceitou, sumindo da tela por causa do erro de outra.
 */
export function withUnread(alerts: readonly InAppAlert[], id: string): InAppAlert[] {
  return alerts.map((a) => (a.id === id ? { ...a, readAt: null } : a))
}

/**
 * O retrato que um palpite otimista tira do mundo antes de arriscar.
 *
 * `gen` é a identidade dona do estado; `serverEpoch` é quantas vezes o servidor
 * já falou. Os dois juntos respondem "o mundo é o mesmo de quando eu chutei?".
 */
export interface OptimisticSnapshot {
  gen: number
  serverEpoch: number
}

/**
 * Desfazer um palpite otimista só é legítimo enquanto NADA falou depois dele.
 *
 * O `markRead` responde na hora e conserta depois se o motor recusar. Só que
 * "conserta" quer dizer restaurar um retrato — e um retrato é o mundo de
 * ANTES. Se entre o palpite e a recusa o servidor respondeu (uma releitura
 * aterrissou) ou a identidade trocou (`reset`), esse retrato virou passado:
 *
 * - restaurá-lo apaga a resposta MAIS NOVA do servidor por cima com dados
 *   velhos — o mesmo defeito que o `gen` já barra no caminho da busca, só que
 *   escondido no caminho de erro;
 * - depois de um `reset`, seria pior ainda: ressuscitaria a lista do dono
 *   ANTERIOR dentro da sessão de quem acabou de entrar.
 *
 * Quando não dá para reverter, a saída certa é **não fazer nada**: a resposta
 * do servidor é mais nova que o palpite e já é a verdade na tela. O usuário vê
 * o estado real; o que se perde é só o desfazer de um chute que o mundo já
 * tornou irrelevante.
 */
export function canRevertOptimistic(
  snapshot: OptimisticSnapshot,
  now: OptimisticSnapshot,
): boolean {
  return snapshot.gen === now.gen && snapshot.serverEpoch === now.serverEpoch
}

/**
 * O que fazer com um empurrão ao vivo do motor.
 *
 * - `ignore`  — já conhecemos esse alerta; reentrega não pode mexer em nada.
 * - `refetch` — perguntar ao motor. SEMPRE, com painel aberto ou fechado.
 *
 * NÃO existe saída "soma 1 no contador". Existiu, e era um defeito: o número
 * no sino é uma AFIRMAÇÃO sobre o banco ("você tem N pendências"), e um número
 * que o cliente incrementa sozinho é afirmação sem lastro — fica errado no
 * instante em que o mesmo usuário lê o alerta em outra aba, em que o fan-out
 * não incluiu este usuário, ou em que o empurrão chega duplicado por reconexão.
 * O contador só pode vir de `unreadCount`, que o motor calcula contando linhas.
 * O empurrão diz "mudou", nunca "quanto".
 *
 * Repare também no que NUNCA sai daqui: `open`. Nenhum caminho de chegada de
 * alerta abre o painel — nem para crítico. É o invariante que este produto
 * perdeu uma vez, do lado do motor, quando o fork de pendências sequestrava o
 * turno do usuário.
 */
export type PushOutcome = 'ignore' | 'refetch'

export function pushOutcome(
  event: InAppAlertCreatedEvent,
  known: readonly InAppAlert[],
): PushOutcome {
  return known.some((a) => a.id === event.alertId) ? 'ignore' : 'refetch'
}

/**
 * O sino só existe quando há o que mostrar — ou quando não sabemos.
 *
 * Um ícone permanente que quase sempre abre uma lista vazia é ruído fixo na
 * moldura do produto: ensina o usuário a ignorá-lo, e aí ele deixa de funcionar
 * no dia em que tem conteúdo. Sem nada, o sino não ocupa lugar nenhum. Isso
 * também cobre o intervalo entre montar e a primeira resposta do motor: não há
 * piscada de sino vazio antes de sabermos se há alerta.
 *
 * O critério é "há o que mostrar", não "há não lido": um alerta já lido mantém
 * o sino de pé, senão a lista sumiria debaixo do cursor no instante em que o
 * usuário terminasse de ler, e ele perderia o acesso ao que acabou de ver.
 *
 * E o terceiro caso, que é o que separa esta regra de um defeito: quando a
 * busca FALHOU, nós não sabemos se há pendência. Sumir aqui transformaria uma
 * falha de rede em silêncio — havendo 3 críticas em aberto, o usuário nunca
 * ficaria sabendo. Então o sino fica de pé, sem numeral, e o painel oferece
 * "tentar de novo". Ausência de dado nunca é tratada como ausência de alerta.
 */
export function shouldShowBell(input: {
  alerts: readonly InAppAlert[]
  unreadCount: number
  error: string | null
  /** True quando o painel está aberto neste instante. */
  open: boolean
}): boolean {
  if (input.unreadCount > 0 || input.alerts.length > 0) return true
  // Painel aberto ganha de tudo: nunca se desmonta a interface debaixo de quem
  // está interagindo com ela. Se o último alerta for lido e removido enquanto
  // ele olha, o painel fica de pé dizendo que está vazio, e some no próximo
  // fechamento — não desaparece no meio do gesto.
  if (input.open) return true
  return input.error !== null
}
